import type { BulkOperation, BulkOperationSummaryDetails } from '@yawi/types';
import { AlertTriangle, CheckCircle, Download, XCircle } from 'lucide-react';
import { downloadBlob } from '@/core/utils/download';

interface ImportResultsProps {
  operation: BulkOperation;
}

const RESOURCE_LABELS: Record<string, string> = {
  units: 'Unidades',
  residents: 'Residentes',
  amenities: 'Amenidades',
  payments: 'Pagos',
  users: 'Usuarios',
};

export function ImportResults({ operation }: ImportResultsProps) {
  const { summary, errors, status } = operation;

  function downloadCredentials() {
    if (!summary?.credentials?.length) return;
    const header = 'Nombre,Email,Unidad,Contraseña Temporal\n';
    const rows = summary.credentials
      .map((c) => `"${c.name ?? ''}","${c.email}","${c.unit ?? ''}","${c.password}"`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `credenciales-${operation.id.slice(0, 8)}.csv`);
  }

  const isSuccess = status === 'COMPLETED';
  const isFailed = status === 'FAILED';

  return (
    <div className="space-y-4">
      {/* Status header */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${
        isSuccess
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}>
        {isSuccess ? (
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
        )}
        <div>
          <p className={`font-semibold text-sm ${isSuccess ? 'text-green-800' : 'text-red-800'}`}>
            {isSuccess ? 'Importación completada' : 'Importación fallida'}
          </p>
          {operation.fileName && (
            <p className="text-xs text-theme-secondary">{operation.fileName}</p>
          )}
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-theme">{summary.totalRows}</p>
            <p className="text-xs text-theme-secondary mt-1">Total filas</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{summary.successRows}</p>
            <p className="text-xs text-green-600 mt-1">Exitosas</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{summary.failedRows}</p>
            <p className="text-xs text-red-600 mt-1">Fallidas</p>
          </div>
        </div>
      )}

      {/* Details breakdown */}
      {summary?.details && Object.keys(summary.details).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-theme mb-2">Detalle por categoría</h4>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 text-theme-secondary font-medium">Categoría</th>
                  <th className="text-center px-4 py-2 text-green-700 font-medium">Creados</th>
                  <th className="text-center px-4 py-2 text-yellow-700 font-medium">Omitidos</th>
                  <th className="text-center px-4 py-2 text-red-700 font-medium">Fallidos</th>
                </tr>
              </thead>
              <tbody>
                {(Object.entries(summary.details) as [string, BulkOperationSummaryDetails][]).map(([key, val]) => (
                  <tr key={key} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2 font-medium text-theme">
                      {RESOURCE_LABELS[key] ?? key}
                    </td>
                    <td className="px-4 py-2 text-center text-green-700">{val.created}</td>
                    <td className="px-4 py-2 text-center text-yellow-700">{val.skipped}</td>
                    <td className="px-4 py-2 text-center text-red-700">{val.failed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Credentials (INITIAL_SETUP only) */}
      {summary?.credentials && summary.credentials.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              <strong>Importante:</strong> Estas credenciales temporales son generadas una sola vez y no pueden ser recuperadas después. Descárgalas ahora.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-theme">
              Credenciales generadas ({summary.credentials.length})
            </h4>
            <button
              onClick={downloadCredentials}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Descargar CSV
            </button>
          </div>
          <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  {summary.credentials[0]?.name !== undefined && (
                    <th className="text-left px-4 py-2 text-theme-secondary font-medium">Nombre</th>
                  )}
                  <th className="text-left px-4 py-2 text-theme-secondary font-medium">Email</th>
                  {summary.credentials[0]?.unit !== undefined && (
                    <th className="text-left px-4 py-2 text-theme-secondary font-medium">Unidad</th>
                  )}
                  <th className="text-left px-4 py-2 text-theme-secondary font-medium">Contraseña</th>
                </tr>
              </thead>
              <tbody>
                {summary.credentials.map((cred, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    {cred.name !== undefined && (
                      <td className="px-4 py-2 text-theme">{cred.name}</td>
                    )}
                    <td className="px-4 py-2 text-theme">{cred.email}</td>
                    {cred.unit !== undefined && (
                      <td className="px-4 py-2 text-theme">{cred.unit}</td>
                    )}
                    <td className="px-4 py-2 font-mono text-xs text-theme">{cred.password}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Errors */}
      {errors && errors.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-red-700 mb-2">
            Errores ({errors.length})
          </h4>
          <div className="border border-red-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-50 border-b border-red-200 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 text-red-700 font-medium">Fila</th>
                  <th className="text-left px-4 py-2 text-red-700 font-medium">Campo</th>
                  <th className="text-left px-4 py-2 text-red-700 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {errors.slice(0, 100).map((err, i) => (
                  <tr key={i} className="border-b border-red-100 last:border-0">
                    <td className="px-4 py-2 text-theme font-mono">{err.row}</td>
                    <td className="px-4 py-2 text-theme-secondary">{err.field ?? '—'}</td>
                    <td className="px-4 py-2 text-red-700">{err.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
